/**
 * User Types for MEV-Med System
 * Types for user management, bindings, and password operations
 */

// ==================== ENUMS ====================

/**
 * User status enumeration (aligned with backend)
 */
export enum UserStatus {
  ATIVO = 'Ativo',
  INATIVO = 'Inativo',
  PENDENTE = 'Pendente',
  SUSPENSO = 'Suspenso',
  BLOQUEADO = 'Bloqueado'
}

/**
 * Binding entity type enumeration
 */
export enum BindingEntityType {
  COMPANY = 'company',
  PROVIDER = 'provider'
}

/**
 * Binding status enumeration
 */
export enum BindingStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

/**
 * Binding function enumeration
 * Represents the functional role/position of a user within an entity
 */
export enum BindingFunction {
  EMPLOYEE = 'employee',
  MANAGER = 'manager',
  ADMIN = 'admin',
  DOCTOR = 'doctor',
  TECHNICIAN = 'technician',
  CONSULTANT = 'consultant',
  DIRECTOR = 'director',
  COORDINATOR = 'coordinator'
}

/**
 * Labels for binding functions in Portuguese
 */
export const BINDING_FUNCTION_LABELS: Record<BindingFunction, string> = {
  [BindingFunction.EMPLOYEE]: 'Funcionário',
  [BindingFunction.MANAGER]: 'Gestor',
  [BindingFunction.ADMIN]: 'Administrador',
  [BindingFunction.DOCTOR]: 'Médico',
  [BindingFunction.TECHNICIAN]: 'Técnico',
  [BindingFunction.CONSULTANT]: 'Consultor',
  [BindingFunction.DIRECTOR]: 'Diretor',
  [BindingFunction.COORDINATOR]: 'Coordenador'
};

/**
 * Get display label for a binding function
 */
export const getBindingFunctionLabel = (fn: BindingFunction | string): string => {
  return BINDING_FUNCTION_LABELS[fn as BindingFunction] || fn;
};

// ==================== INTERFACES ====================

/**
 * Entity details for bindings (company or provider info)
 */
export interface BindingEntityDetails {
  id: number;
  name: string;
  cnpj?: string;
  type?: string;
}

/**
 * User binding interface
 */
export interface UserBinding {
  id: number;
  userId: number;
  entityType: BindingEntityType;
  entityId: number;
  function: BindingFunction;
  functionLabel?: string;
  status: BindingStatus;
  startedAt: string;
  endedAt?: string;
  entityDetails?: BindingEntityDetails;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Display name of the entity a binding points to.
 * Without a name, says what it is — the internal id never reaches the user.
 */
export const getBindingEntityName = (
  binding: Pick<UserBinding, 'entityType' | 'entityDetails'>
): string => {
  if (binding.entityDetails?.name) return binding.entityDetails.name;
  return binding.entityType === BindingEntityType.COMPANY
    ? 'Empresa não identificada'
    : 'Clínica não identificada';
};

/**
 * Role information for display
 */
export interface UserRole {
  id: number;
  name: string;
  displayName: string;
}

/**
 * Main User interface (aligned with backend)
 */
export interface User {
  id: number;
  name: string;
  email: string;
  cpf: string;
  whatsapp?: string;
  situacao: UserStatus;
  avatarPath?: string;
  createdAt: string;
  updatedAt: string;
  // Optional relations
  bindings?: UserBinding[];
  roles?: UserRole[];
  permissions?: string[];
}

/**
 * User list filters (aligned with backend query params)
 */
export interface UserFilters {
  search?: string;
  situacao?: UserStatus | UserStatus[];
  companyId?: number | number[];
  providerId?: number | number[];
}

/**
 * User list parameters with pagination
 */
export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: UserFilters;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Create user request (aligned with backend)
 */
export interface CreateUserRequest {
  name: string;
  email: string;
  cpf: string;
  password?: string;
  whatsapp?: string;
  // Initial binding (optional)
  companyId?: number;
  providerId?: number;
  /** Binding function (job role within entity) */
  bindingFunction?: BindingFunction;
}

/**
 * Create user response (includes temporary password info)
 */
export interface CreateUserResponse {
  user: User;
  temporaryPassword?: string;
}

/**
 * Update user request (aligned with backend)
 * Note: email and cpf cannot be changed
 */
export interface UpdateUserRequest {
  name?: string;
  whatsapp?: string;
  situacao?: UserStatus;
  avatarPath?: string;
}

/**
 * Add binding request
 */
export interface AddBindingRequest {
  entityType: BindingEntityType;
  entityId: number;
  function: BindingFunction;
}

/**
 * User form data (for React Hook Form)
 */
export interface UserFormData {
  name: string;
  email: string;
  cpf: string;
  password: string;
  confirmPassword: string;
  whatsapp: string;
  // Initial binding
  bindingEntityType: BindingEntityType | '';
  bindingEntityId: string;
  bindingFunction: BindingFunction | '';
}

/**
 * User edit form data (for React Hook Form)
 */
export interface UserEditFormData {
  name: string;
  whatsapp: string;
  situacao: UserStatus;
}

/**
 * Binding form data (for modal)
 */
export interface BindingFormData {
  entityType: BindingEntityType | '';
  entityId: string;
  function: BindingFunction | '';
}

// ==================== LABELS ====================

/**
 * User status labels (Portuguese)
 */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ATIVO]: 'Ativo',
  [UserStatus.INATIVO]: 'Inativo',
  [UserStatus.PENDENTE]: 'Pendente',
  [UserStatus.SUSPENSO]: 'Suspenso',
  [UserStatus.BLOQUEADO]: 'Bloqueado',
};

/**
 * Role labels (Portuguese) - Maps role names to display names
 */
export const ROLE_LABELS: Record<string, string> = {
  // RBAC roles (global permissions)
  super_admin: 'Super Admin',
  gestor_clinica: 'Gestor Clínica',
  gestor_empresa: 'Gestor Empresa',
  profissional_saude: 'Profissional de Saúde',
  recepcao: 'Recepção',
  // Binding roles (contextual within entities)
  binding_employee: 'Funcionário',
  binding_manager: 'Gestor',
  binding_admin: 'Administrador',
  binding_doctor: 'Médico',
  binding_technician: 'Técnico',
  binding_consultant: 'Consultor',
  binding_director: 'Diretor',
  binding_coordinator: 'Coordenador',
};

/**
 * Get display label for a role
 */
export const getRoleLabel = (roleName: string): string => {
  return ROLE_LABELS[roleName] || roleName;
};

/**
 * Binding entity type labels (Portuguese)
 */
export const BINDING_ENTITY_TYPE_LABELS: Record<BindingEntityType, string> = {
  [BindingEntityType.COMPANY]: 'Empresa',
  [BindingEntityType.PROVIDER]: 'Prestador',
};

/**
 * Binding status labels (Portuguese)
 */
export const BINDING_STATUS_LABELS: Record<BindingStatus, string> = {
  [BindingStatus.ACTIVE]: 'Ativo',
  [BindingStatus.INACTIVE]: 'Inativo',
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format CPF with mask: XXX.XXX.XXX-XX
 */
export const formatCPF = (value: string): string => {
  const numericOnly = value.replace(/\D/g, '');

  if (numericOnly.length <= 3) {
    return numericOnly;
  } else if (numericOnly.length <= 6) {
    return `${numericOnly.slice(0, 3)}.${numericOnly.slice(3)}`;
  } else if (numericOnly.length <= 9) {
    return `${numericOnly.slice(0, 3)}.${numericOnly.slice(3, 6)}.${numericOnly.slice(6)}`;
  } else {
    return `${numericOnly.slice(0, 3)}.${numericOnly.slice(3, 6)}.${numericOnly.slice(6, 9)}-${numericOnly.slice(9, 11)}`;
  }
};

/**
 * Remove CPF mask
 */
export const removeCPFMask = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

/**
 * Format WhatsApp/phone with mask: (XX) XXXXX-XXXX
 */
export const formatWhatsApp = (value: string): string => {
  const numericOnly = value.replace(/\D/g, '');

  if (numericOnly.length <= 2) {
    return numericOnly.length > 0 ? `(${numericOnly}` : '';
  } else if (numericOnly.length <= 7) {
    return `(${numericOnly.slice(0, 2)}) ${numericOnly.slice(2)}`;
  } else {
    return `(${numericOnly.slice(0, 2)}) ${numericOnly.slice(2, 7)}-${numericOnly.slice(7, 11)}`;
  }
};

/**
 * Remove WhatsApp mask
 */
export const removeWhatsAppMask = (whatsapp: string): string => {
  return whatsapp.replace(/\D/g, '');
};

/**
 * Validate CPF algorithm
 */
export const validateCPF = (cpf: string): boolean => {
  const numericCPF = cpf.replace(/\D/g, '');

  if (numericCPF.length !== 11) return false;

  // Check for known invalid CPFs
  if (/^(\d)\1{10}$/.test(numericCPF)) return false;

  // Validate first digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numericCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numericCPF.charAt(9))) return false;

  // Validate second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numericCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numericCPF.charAt(10))) return false;

  return true;
};

/**
 * Get status color for chips
 */
export const getUserStatusColor = (status: UserStatus): 'success' | 'default' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case UserStatus.ATIVO:
      return 'success';
    case UserStatus.INATIVO:
      return 'default';
    case UserStatus.PENDENTE:
      return 'info';
    case UserStatus.SUSPENSO:
      return 'warning';
    case UserStatus.BLOQUEADO:
      return 'error';
    default:
      return 'default';
  }
};
